import { DocumentAsset, DocumentAssetType } from './types';

export const MAX_DOCUMENT_FILE_SIZE = 15 * 1024 * 1024;

const DB_NAME = 'specmap-documents';
const DB_VERSION = 1;
const STORE_NAME = 'documents';

const WORD_MIME_TYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

export interface StoredDocumentAsset extends DocumentAsset {
  blob: Blob;
}

function extensionOf(name: string) {
  return name.toLowerCase().split('.').pop() ?? '';
}

export function detectDocumentType(file: Pick<File, 'name' | 'type'>): DocumentAssetType | null {
  const extension = extensionOf(file.name);

  if (extension === 'md' || file.type === 'text/markdown' || file.type === 'text/plain') {
    return 'markdown';
  }

  if (extension === 'pdf' || file.type === 'application/pdf') {
    return 'pdf';
  }

  if (extension === 'doc' || extension === 'docx' || WORD_MIME_TYPES.has(file.type)) {
    return 'word';
  }

  return null;
}

export function validateDocumentFile(file: Pick<File, 'name' | 'type' | 'size'>) {
  if (file.size > MAX_DOCUMENT_FILE_SIZE) {
    throw new Error('Document must be 15 MB or smaller.');
  }

  if (!detectDocumentType(file)) {
    throw new Error('Upload a PDF, Word document, or Markdown file.');
  }

  if (!file.name.trim()) {
    throw new Error('Document file name is required.');
  }
}

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('specMapId', 'specMapId', { unique: false });
      }
    };

    request.onerror = () => reject(request.error ?? new Error('Could not open document store.'));
    request.onsuccess = () => resolve(request.result);
  });
}

function runStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T> | void
) {
  return openDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode);
        const store = transaction.objectStore(STORE_NAME);
        const request = operation(store);

        transaction.oncomplete = () => {
          db.close();
          resolve(request ? request.result : (undefined as T));
        };
        transaction.onerror = () => {
          db.close();
          reject(transaction.error ?? new Error('Document store operation failed.'));
        };
      })
  );
}

export async function saveDocumentAsset(
  specMapId: string,
  file: File,
  uploadedBy = 'Kavinu Wethmin'
): Promise<DocumentAsset> {
  validateDocumentFile(file);

  const type = detectDocumentType(file);
  if (!type) {
    throw new Error('Upload a PDF, Word document, or Markdown file.');
  }

  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? `doc-${crypto.randomUUID()}`
      : `doc-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const contentText = type === 'markdown' ? await file.text() : undefined;
  const asset: StoredDocumentAsset = {
    id,
    specMapId,
    name: file.name,
    type,
    mimeType: file.type || fallbackMimeType(file.name, type),
    size: file.size,
    uploadedAt: new Date().toISOString(),
    uploadedBy,
    storageMode: 'indexeddb',
    contentText,
    blob: file,
  };

  await runStore('readwrite', (store) => store.put(asset));
  return toPublicAsset(asset);
}

export async function listDocumentAssets(specMapId: string): Promise<DocumentAsset[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const index = transaction.objectStore(STORE_NAME).index('specMapId');
    const request = index.getAll(specMapId);

    request.onsuccess = () => {
      db.close();
      resolve(request.result.map(toPublicAsset).sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()));
    };
    request.onerror = () => {
      db.close();
      reject(request.error ?? new Error('Could not list documents.'));
    };
  });
}

export async function getDocumentAsset(id: string): Promise<StoredDocumentAsset | null> {
  return runStore<StoredDocumentAsset | undefined>('readonly', (store) => store.get(id)).then(
    (asset) => asset ?? null
  );
}

export async function deleteDocumentAsset(id: string) {
  await runStore('readwrite', (store) => store.delete(id));
}

export async function downloadDocumentAsset(id: string) {
  const asset = await getDocumentAsset(id);

  if (!asset) {
    throw new Error('Document not found.');
  }

  const url = URL.createObjectURL(asset.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = asset.name;
  anchor.click();
  URL.revokeObjectURL(url);
}

function fallbackMimeType(name: string, type: DocumentAssetType) {
  if (type === 'markdown') {
    return 'text/markdown';
  }

  if (type === 'pdf') {
    return 'application/pdf';
  }

  return extensionOf(name) === 'doc' ? 'application/msword' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
}

function toPublicAsset(asset: StoredDocumentAsset): DocumentAsset {
  const { blob: _blob, ...publicAsset } = asset;
  return publicAsset;
}
